import { LoadingTheme } from '#layouts/main-layout/LoadingTheme';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';
import { isEmpty } from 'lodash-es';
import * as React from 'react';
import { useContext } from 'react';
import { Dictionary, QuestionnaireElements, QuestionnaireTheme } from '../models/questionnaire';
import { useCurrentTheme } from '../queries/questionnaire';
import { useTouchDevice } from '../utils/breakpoint';
import { loadGoogleFont } from '../utils/loadGoogleFont';
import { toQuestionnaireElements } from '../utils/toQuestionnaireElements';

const ThemeContext = React.createContext<QuestionnaireElements>({} as QuestionnaireElements);

export const useThemeContext = () => useContext(ThemeContext);

export const ThemeProvider = ({
  children,
  appImages,
  googleKey,
  isEmbed,
  hideHeader,
  dataTheme,
  isLoading,
  hasLocalStorage,
  currency,
  defaultPicture
}: {
  children: React.ReactNode;
  appImages: Dictionary;
  googleKey?: string;
  isEmbed?: boolean;
  hideHeader?: boolean;
  dataTheme?: QuestionnaireTheme;
  isLoading: boolean;
  hasLocalStorage?: boolean;
  currency: { name: string; symbol: string };
  defaultPicture: string;
}) => {
  const { data: currentTheme, isLoading: isLoadingCurrentTheme } = useCurrentTheme(!!hasLocalStorage);
  const isTouchDevice = useTouchDevice();

  const elements: QuestionnaireElements = React.useMemo(() => {
    return {
      ...toQuestionnaireElements(!isEmpty(dataTheme) ? dataTheme : currentTheme || {}),
      isTouchDevice,
      appImages,
      googleKey,
      isEmbed,
      hideHeader,
      currency,
      defaultPicture
    };
  }, [currentTheme, dataTheme, appImages, googleKey, isEmbed, hideHeader, currency, defaultPicture]);

  // Build font-family with fallback chain
  const buildFontFamily = (font: string | undefined | null): string => {
    if (!font || !font.trim() || font.trim() === 'Open Sans') {
      return '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
    }

    // If the font already includes fallbacks (contains a comma), use as-is
    if (font.includes(',')) {
      return font;
    }

    // Otherwise, add a comprehensive fallback chain
    return `"${font}", "Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`;
  };

  // Load Google Font if needed
  React.useEffect(() => {
    loadGoogleFont(elements.fontTypeface);
  }, [elements.fontTypeface]);

  // Create MUI theme with custom font family
  const muiTheme = React.useMemo(() => {
    const fontFamily = buildFontFamily(elements.fontTypeface);

    return createTheme({
      typography: {
        fontFamily
      }
    });
  }, [elements.fontTypeface]);

  return (
    <ThemeContext.Provider value={elements}>
      <MuiThemeProvider theme={muiTheme}>
        {isEmpty(dataTheme) && !isLoadingCurrentTheme && (
          <LoadingTheme open={isLoading} primaryButtonBackground={elements.primaryButtonBackground} />
        )}
        {(!isEmpty(dataTheme) || isLoadingCurrentTheme) && children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
